<template>
  <div class="superadmin-dashboard">
    <!-- Platform Operator Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom border-subtle">
      <div>
        <h3 class="text-white font-weight-bold mb-1">
          {{ $t('superadmin.title') }}
        </h3>
        <p class="text-muted small mb-0">
          {{ $t('superadmin.subtitle') }}
        </p>
      </div>

      <div class="d-flex align-items-center mt-3 mt-md-0">
        <button
          class="btn btn-outline-secondary btn-sm mr-2 px-3"
          @click="loadAllData"
          :disabled="loading"
        >
          <i class="mdi mdi-refresh mr-1" :class="{ 'mdi-spin': loading }"></i>
          {{ loading ? $t('common.loading') : $t('common.refresh') }}
        </button>

        <router-link
          to="/owner/add"
          class="btn btn-primary btn-sm px-3 font-weight-bold"
        >
          <i class="mdi mdi-plus-circle-outline mr-1"></i>
          {{ $t('superadmin.onboardClientBtn') }}
        </router-link>
      </div>
    </div>

    <!-- Top SaaS Financial & Scale KPI Cards -->
    <div class="row mb-4">
      <!-- MRR -->
      <div class="col-xl-3 col-sm-6 grid-margin stretch-card">
        <div class="card shadow-sm h-100">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <p class="text-muted mb-1 small font-weight-bold text-uppercase">
                  {{ $t('superadmin.mrr') }}
                </p>
                <h3 class="mb-0 text-white font-weight-bold">
                  {{ formatRupiah(revenueData.mrr) }}
                </h3>
              </div>
              <div class="p-2 rounded-lg" style="background: rgba(16, 185, 129, 0.12); border: 1px solid rgba(16, 185, 129, 0.25);">
                <i class="mdi mdi-cash-multiple text-success" style="font-size: 22px;"></i>
              </div>
            </div>
            <div class="mt-2 text-muted small">
              <span class="text-success font-weight-bold">
                <i class="mdi mdi-arrow-up-bold" aria-hidden="true"></i> {{ $t('superadmin.activeMrrLabel') }}
              </span>
              • {{ revenueData.active_subscriptions ?? 0 }} {{ $t('superadmin.subscriptionStatus') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Total Platform Revenue -->
      <div class="col-xl-3 col-sm-6 grid-margin stretch-card">
        <div class="card shadow-sm h-100">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <p class="text-muted mb-1 small font-weight-bold text-uppercase">
                  {{ $t('superadmin.totalRevenue') }}
                </p>
                <h3 class="mb-0 text-white font-weight-bold">
                  {{ formatRupiah(revenueData.total_revenue) }}
                </h3>
              </div>
              <div class="p-2 rounded-lg" style="background: rgba(99, 102, 241, 0.12); border: 1px solid rgba(99, 102, 241, 0.25);">
                <i class="mdi mdi-chart-line text-primary" style="font-size: 22px;" aria-hidden="true"></i>
              </div>
            </div>
            <div class="mt-2 text-muted small">
              <span class="text-info font-weight-bold">{{ $t('superadmin.allTime') }}</span> {{ $t('superadmin.allTimeBillingVolume') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Active Client Enterprises -->
      <div class="col-xl-3 col-sm-6 grid-margin stretch-card">
        <div class="card shadow-sm h-100">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <p class="text-muted mb-1 small font-weight-bold text-uppercase">
                  {{ $t('superadmin.activeTenants') }}
                </p>
                <h3 class="mb-0 text-warning font-weight-bold">
                  {{ platformStats.companies_count ?? 0 }} <span class="text-muted h6 font-weight-normal">{{ $t('superadmin.tenants') }}</span>
                </h3>
              </div>
              <div class="p-2 rounded-lg" style="background: rgba(245, 158, 11, 0.12); border: 1px solid rgba(245, 158, 11, 0.25);">
                <i class="mdi mdi-domain text-warning" style="font-size: 22px;" aria-hidden="true"></i>
              </div>
            </div>
            <div class="mt-2 text-muted small">
              <router-link to="/owner" class="text-warning text-decoration-none">
                {{ $t('superadmin.viewAllEnterprises') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Global Platform Users -->
      <div class="col-xl-3 col-sm-6 grid-margin stretch-card">
        <div class="card shadow-sm h-100">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <p class="text-muted mb-1 small font-weight-bold text-uppercase">
                  {{ $t('superadmin.totalGlobalUsers') }}
                </p>
                <h3 class="mb-0 text-info font-weight-bold">
                  {{ platformStats.users?.total ?? 0 }} <span class="text-muted h6 font-weight-normal">{{ $t('superadmin.usersLabel') }}</span>
                </h3>
              </div>
              <div class="p-2 rounded-lg" style="background: rgba(14, 165, 233, 0.12); border: 1px solid rgba(14, 165, 233, 0.25);">
                <i class="mdi mdi-account-group text-info" style="font-size: 22px;" aria-hidden="true"></i>
              </div>
            </div>
            <div class="mt-2 text-muted small">
              {{ platformStats.users?.players ?? 0 }} {{ $t('superadmin.playersLabel') }} • {{ platformStats.users?.makers ?? 0 }} {{ $t('superadmin.managersLabel') }} • {{ platformStats.users?.owners ?? 0 }} {{ $t('superadmin.ownersLabel') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Secondary Platform Telemetry Bar -->
    <div class="row mb-4">
      <div class="col-md-4 mb-2 mb-md-0">
        <div class="p-3 rounded-lg border border-subtle bg-subtle d-flex align-items-center justify-content-between">
          <div>
            <div class="text-muted small font-weight-bold text-uppercase mb-1">
              {{ $t('superadmin.globalCompletionRate') }}
            </div>
            <div class="h4 mb-0 text-white font-weight-bold">
              {{ platformStats.rules?.global_completion_rate ?? 0 }}%
            </div>
          </div>
          <div style="width: 120px;">
            <div class="progress" style="height: 8px;">
              <div
                class="progress-bar bg-success"
                role="progressbar"
                :style="{ width: `${platformStats.rules?.global_completion_rate ?? 0}%` }"
              ></div>
            </div>
            <div class="text-right text-muted mt-1" style="font-size: 10px;">
              {{ platformStats.rules?.total_approved ?? 0 }} / {{ platformStats.rules?.total_submissions ?? 0 }} {{ $t('superadmin.approvedLower') }}
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4 mb-2 mb-md-0">
        <div class="p-3 rounded-lg border border-subtle bg-subtle d-flex align-items-center justify-content-between">
          <div>
            <div class="text-muted small font-weight-bold text-uppercase mb-1">
              {{ $t('superadmin.totalGoldInCirculation') }}
            </div>
            <div class="h4 mb-0 text-warning font-weight-bold">
              <i class="mdi mdi-circle-multiple-outline mr-1" aria-hidden="true"></i>{{ formatNumber(platformStats.total_gold_distributed ?? 0) }} Gold
            </div>
          </div>
          <span class="badge badge-outline-warning">
            {{ $t('superadmin.activeEconomy') }}
          </span>
        </div>
      </div>

      <div class="col-md-4">
        <div class="p-3 rounded-lg border border-subtle bg-subtle d-flex align-items-center justify-content-between">
          <div>
            <div class="text-muted small font-weight-bold text-uppercase mb-1">
              {{ $t('superadmin.platformRules') }}
            </div>
            <div class="h4 mb-0 text-white font-weight-bold">
              {{ platformStats.rules?.total_rules ?? 0 }} {{ $t('superadmin.liveRules') }}
            </div>
          </div>
          <span class="badge badge-outline-info">
            {{ $t('superadmin.acrossAllTenants') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Revenue Performance Chart (Highcharts) -->
    <div class="card mb-4 shadow-sm">
      <div class="card-body p-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 class="card-title text-white font-weight-bold mb-1">
              <i class="mdi mdi-finance text-primary mr-1" aria-hidden="true"></i>
              {{ $t('superadmin.revenueTrendTitle') }} ({{ revenueData.year || currentYear }})
            </h5>
            <small class="text-muted">
              {{ $t('superadmin.monthlyBilledVolume') }}
            </small>
          </div>
          <div>
            <span class="badge badge-success px-3 py-2 font-weight-bold">
              {{ $t('superadmin.allTime') }} {{ formatRupiah(revenueData.total_revenue) }}
            </span>
          </div>
        </div>
        <div id="superadmin-revenue-chart" style="height: 300px; width: 100%;"></div>
      </div>
    </div>

    <!-- Manual Invoices Awaiting Verification Queue -->
    <div class="card mb-4 shadow-sm">
      <div class="card-body p-4">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
          <div>
            <h5 class="card-title text-white font-weight-bold mb-1">
              <i class="mdi mdi-clock-check-outline text-warning mr-1" aria-hidden="true"></i>
              {{ $t('superadmin.pendingBillingTitle') }}
            </h5>
            <p class="text-muted small mb-0">
              {{ $t('superadmin.pendingBillingDesc') }}
            </p>
          </div>
          <div class="mt-2 mt-md-0">
            <router-link to="/billing" class="btn btn-sm btn-outline-warning">
              {{ $t('superadmin.viewAllInvoices') }}
            </router-link>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr class="text-muted small text-uppercase">
                <th>{{ $t('superadmin.invoiceNumber') }}</th>
                <th>{{ $t('superadmin.companyName') }}</th>
                <th>{{ $t('superadmin.package') }}</th>
                <th>{{ $t('superadmin.amount') }}</th>
                <th>{{ $t('superadmin.paymentMethod') }}</th>
                <th>{{ $t('superadmin.subscriptionStatus') }}</th>
                <th class="text-right">{{ $t('superadmin.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in billingQueue" :key="item.id">
                <td class="font-weight-bold text-white">
                  <span class="text-warning">{{ item.billing_transaction?.invoice_number || `INV-${item.id}` }}</span>
                  <div class="text-muted small" style="font-size: 11px;">
                    {{ formatDate(item.created_at) }}
                  </div>
                </td>
                <td>
                  <div class="text-white font-weight-bold">{{ item.company?.name || '-' }}</div>
                  <small class="text-muted">{{ $t('superadmin.quotaPrefix') }}: {{ item.company?.number_of_players || 0 }} {{ $t('superadmin.playersLabel') }}</small>
                </td>
                <td>
                  <span class="badge badge-outline-info font-weight-bold">
                    {{ item.package?.package_name || $t('superadmin.standardPlan') }}
                  </span>
                  <div class="text-muted small mt-1">{{ item.duration || 1 }} {{ $t('superadmin.monthsLabel') }}</div>
                </td>
                <td class="font-weight-bold text-white">
                  {{ formatRupiah(item.billing_transaction?.amount || 0) }}
                </td>
                <td>
                  <span
                    class="badge"
                    :class="item.billing_transaction?.type === 'digital' ? 'badge-primary' : 'badge-secondary'"
                  >
                    {{ item.billing_transaction?.type === 'digital' ? $t('superadmin.onlineGateway') : $t('superadmin.manualTransfer') }}
                  </span>
                </td>
                <td>
                  <span
                    class="badge font-weight-bold"
                    :class="{
                      'badge-warning text-dark': (item.billing_transaction?.status || item.status) === 'pending',
                      'badge-success': (item.billing_transaction?.status || item.status) === 'paid' || (item.billing_transaction?.status || item.status) === 'success',
                      'badge-danger': (item.billing_transaction?.status || item.status) === 'failed' || (item.billing_transaction?.status || item.status) === 'expired'
                    }"
                  >
                    {{ formatInvoiceStatus(item.billing_transaction?.status || item.status) }}
                  </span>
                </td>
                <td class="text-right">
                  <div class="d-inline-flex flex-wrap justify-content-end align-items-center gap-1">
                    <!-- Approve Button if pending -->
                    <button
                      v-if="(item.billing_transaction?.status || item.status) === 'pending'"
                      class="btn btn-sm btn-primary font-weight-bold px-2"
                      @click="handleApprove(item)"
                      :disabled="actionLoading"
                      :title="$t('superadmin.approvePayment')"
                    >
                      <i class="mdi mdi-check-circle-outline mr-1" aria-hidden="true"></i>
                      {{ $t('common.approve') }}
                    </button>

                    <!-- Reject Button if pending -->
                    <button
                      v-if="(item.billing_transaction?.status || item.status) === 'pending'"
                      class="btn btn-sm btn-outline-danger px-2"
                      @click="openRejectModal(item)"
                      :disabled="actionLoading"
                      :title="$t('superadmin.rejectPayment')"
                      :aria-label="$t('superadmin.rejectPayment')"
                    >
                      <i class="mdi mdi-close-circle-outline" aria-hidden="true"></i>
                    </button>

                    <!-- Download Invoice / Proof -->
                    <a
                      v-if="item.billing_transaction?.invoice"
                      :href="item.billing_transaction.invoice"
                      class="btn btn-sm btn-outline-secondary px-2"
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      title="PDF"
                    >
                      <i class="mdi mdi-download mr-1" aria-hidden="true"></i> PDF
                    </a>
                  </div>
                </td>
              </tr>

              <tr v-if="billingQueue.length === 0">
                <td colspan="7" class="text-center py-4 text-muted">
                  <i class="mdi mdi-check-decagram text-success" style="font-size: 32px;" aria-hidden="true"></i>
                  <div class="mt-2">{{ $t('superadmin.noPendingInvoices') }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Client Companies (Mitra) Overview Table -->
    <div class="card mb-4 shadow-sm">
      <div class="card-body p-4">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
          <div>
            <h5 class="card-title text-white font-weight-bold mb-1">
              <i class="mdi mdi-domain text-info mr-1" aria-hidden="true"></i>
              {{ $t('superadmin.allCompanies') }}
            </h5>
            <small class="text-muted">
              {{ $t('superadmin.allCompaniesSub') }}
            </small>
          </div>
          <div class="mt-2 mt-md-0 d-flex">
            <div class="input-group input-group-sm mr-2" style="width: 280px;">
              <div class="input-group-prepend">
                <span class="input-group-text bg-input border-card text-muted">
                  <i class="mdi mdi-magnify" aria-hidden="true"></i>
                </span>
              </div>
              <input
                type="text"
                class="form-control"
                :placeholder="$t('superadmin.searchCompanies')"
                v-model="companySearch"
              />
            </div>
            <router-link to="/owner/add" class="btn btn-sm btn-primary text-nowrap">
              <i class="mdi mdi-plus mr-1" aria-hidden="true"></i> {{ $t('nav.createCompany') }}
            </router-link>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr class="text-muted small text-uppercase">
                <th>#</th>
                <th>{{ $t('superadmin.companyName') }}</th>
                <th>{{ $t('superadmin.ownerContact') }}</th>
                <th>{{ $t('superadmin.currentPlan') }}</th>
                <th>{{ $t('superadmin.playerQuota') }}</th>
                <th>{{ $t('superadmin.healthStatus') }}</th>
                <th class="text-right">{{ $t('superadmin.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(comp, idx) in filteredCompanies" :key="comp.id || idx">
                <td class="text-muted">{{ idx + 1 }}</td>
                <td>
                  <div class="d-flex align-items-center">
                    <div
                      class="rounded-lg p-2 mr-2 text-white font-weight-bold d-flex align-items-center justify-content-center"
                      style="width: 36px; height: 36px; background: rgba(99, 102, 241, 0.2); border: 1px solid rgba(99, 102, 241, 0.4);"
                    >
                      {{ (comp.name || 'G')[0].toUpperCase() }}
                    </div>
                    <div>
                      <div class="text-white font-weight-bold">{{ comp.name }}</div>
                      <small class="text-muted">ID: MITRA-{{ comp.id }} • {{ $t('superadmin.createdPrefix') }}: {{ formatDate(comp.created_at) }}</small>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="text-white small font-weight-bold">
                    {{ comp.owner?.name || '-' }}
                  </div>
                  <small class="text-muted">{{ comp.owner?.email || '-' }}</small>
                </td>
                <td>
                  <span
                    class="badge font-weight-bold"
                    :class="getCompanyPlanBadgeClass(comp)"
                  >
                    {{ resolveCompanyPlanName(comp) }}
                  </span>
                  <div class="text-muted small mt-1">
                    <template v-if="resolveCompanyExpiry(comp)">
                      {{ $t('superadmin.activeUntilPrefix') }}: {{ formatDate(resolveCompanyExpiry(comp)) }}
                    </template>
                    <template v-else>
                      {{ $t('superadmin.noExpirySet') }}
                    </template>
                  </div>
                </td>
                <td style="min-width: 140px;">
                  <div class="d-flex justify-content-between small text-white mb-1">
                    <span>{{ comp.users_count ?? 0 }} / {{ comp.number_of_players ?? 0 }}</span>
                    <span class="text-muted">{{ comp.number_of_players > 0 ? Math.round(((comp.users_count ?? 0) / comp.number_of_players) * 100) : 0 }}%</span>
                  </div>
                  <div class="progress" style="height: 6px;">
                    <div
                      class="progress-bar bg-info"
                      role="progressbar"
                      :style="{ width: `${comp.number_of_players > 0 ? Math.min(100, Math.round(((comp.users_count ?? 0) / comp.number_of_players) * 100)) : 0}%` }"
                    ></div>
                  </div>
                </td>
                <td>
                  <span class="badge" :class="getCompanyHealthBadgeClass(comp)">
                    <i class="mdi mr-1" :class="getCompanyHealthIcon(comp)" aria-hidden="true"></i>{{ getCompanyHealthLabel(comp) }}
                  </span>
                </td>
                <td class="text-right">
                  <router-link
                    :to="{ name: 'DetailOwner', params: { id: comp.id } }"
                    class="btn btn-sm btn-outline-secondary mr-1"
                  >
                    <i class="mdi mdi-eye mr-1" aria-hidden="true"></i>{{ $t('common.view') }}
                  </router-link>
                  <router-link
                    :to="{ name: 'EditOwner', params: { id: comp.id } }"
                    class="btn btn-sm btn-outline-warning"
                  >
                    <i class="mdi mdi-pencil mr-1" aria-hidden="true"></i>{{ $t('common.edit') }}
                  </router-link>
                </td>
              </tr>

              <tr v-if="filteredCompanies.length === 0">
                <td colspan="7" class="text-center py-4 text-muted">
                  {{ $t('common.noData') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Reject Billing Modal -->
    <div
      v-if="showRejectModal"
      class="custom-modal-backdrop d-flex align-items-center justify-content-center"
      style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.75); z-index: 1050; backdrop-filter: blur(4px);"
    >
      <div class="card border border-subtle shadow-xl text-white" style="width: 480px; max-width: 90%;">
        <div class="card-header d-flex justify-content-between align-items-center py-3">
          <h5 class="mb-0 text-danger font-weight-bold">
            <i class="mdi mdi-alert-circle mr-1" aria-hidden="true"></i>
            {{ $t('superadmin.rejectPayment') }}
          </h5>
          <button
            type="button"
            class="btn btn-xs btn-outline-secondary rounded-circle"
            style="width: 28px; height: 28px; padding: 0;"
            @click="closeRejectModal"
            :aria-label="$t('common.close')"
          >
            ✕
          </button>
        </div>
        <div class="card-body p-4">
          <p class="small text-muted mb-3">
            {{ $t('superadmin.invoiceNumber') }}: <strong class="text-white">{{ activeRejectItem?.billing_transaction?.invoice_number || activeRejectItem?.id }}</strong>
          </p>
          <div class="form-group mb-0">
            <label for="reject-reason-textarea" class="small text-muted font-weight-bold text-uppercase">
              {{ $t('superadmin.rejectionReasonLabel') }}
            </label>
            <textarea
              id="reject-reason-textarea"
              v-model="rejectReason"
              class="form-control"
              rows="3"
              :placeholder="$t('superadmin.rejectionReasonPlaceholder')"
            ></textarea>
          </div>
        </div>
        <div class="card-footer d-flex justify-content-end py-3">
          <button class="btn btn-outline-secondary btn-sm mr-2" @click="closeRejectModal">
            {{ $t('common.cancel') }}
          </button>
          <button
            class="btn btn-danger btn-sm font-weight-bold"
            @click="submitReject"
            :disabled="!rejectReason || actionLoading"
          >
            {{ $t('superadmin.confirmRejection') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Highcharts from "highcharts";
import moment from "moment";
import Swal from "sweetalert2";
import {
  getSuperAdminRevenue,
  getSuperAdminPlatformStats,
  getSuperAdminCompanies,
  getPendingBillingApprovals,
  approveBilling,
  rejectBilling
} from "@/services/superadminService";

export default {
  name: "SuperAdminDashboardView",
  data() {
    return {
      loading: false,
      actionLoading: false,
      companySearch: "",
      currentYear: new Date().getFullYear(),
      revenueData: {
        total_revenue: 0,
        mrr: 0,
        active_subscriptions: 0,
        year: new Date().getFullYear(),
        monthly_revenue: {},
        recent_transactions: []
      },
      platformStats: {
        companies_count: 0,
        users: { total: 0, players: 0, makers: 0, owners: 0 },
        rules: { total_rules: 0, total_submissions: 0, total_approved: 0, global_completion_rate: 0 },
        total_gold_distributed: 0
      },
      billingQueue: [],
      companies: [],
      showRejectModal: false,
      activeRejectItem: null,
      rejectReason: "",
      chartInstance: null
    };
  },
  watch: {
    "$currentLocale"() {
      this.$nextTick(() => {
        this.renderRevenueChart();
      });
    }
  },
  computed: {
    filteredCompanies() {
      if (!this.companySearch) return this.companies;
      const q = this.companySearch.toLowerCase();
      return this.companies.filter((c) => {
        const name = (c.name || "").toLowerCase();
        const ownerName = (c.owner?.name || "").toLowerCase();
        const ownerEmail = (c.owner?.email || "").toLowerCase();
        return name.includes(q) || ownerName.includes(q) || ownerEmail.includes(q);
      });
    }
  },
  mounted() {
    this.loadAllData();
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  },
  methods: {
    formatNumber(val) {
      return Number(val || 0).toLocaleString(this.$currentLocale === "id" ? "id-ID" : "en-US");
    },

    formatRupiah(value) {
      const num = Number(value) || 0;
      return new Intl.NumberFormat(this.$currentLocale === "id" ? "id-ID" : "en-US", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num);
    },

    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD MMM YYYY");
    },

    formatInvoiceStatus(status) {
      const s = String(status || "pending").toLowerCase();
      if (s === "paid" || s === "success") return this.$t("common.approved").toUpperCase();
      if (s === "failed" || s === "rejected") return this.$t("common.rejected").toUpperCase();
      if (s === "expired") return this.$t("superadmin.statusExpired").toUpperCase();
      return this.$t("common.pending").toUpperCase();
    },

    resolveCompanyPlanName(comp) {
      const pkg = comp.resolved_package_name || comp.subscription?.billing?.package?.package_name;
      if (pkg) return pkg;
      if (comp.subscription_status === "trial") return this.$t("superadmin.trialPlan");
      return this.$t("superadmin.noActivePlan");
    },

    resolveCompanyExpiry(comp) {
      return comp.resolved_active_until || comp.subscription?.active_until_date || comp.expired_date || null;
    },

    getCompanyPlanBadgeClass(comp) {
      const subStatus = comp.subscription_status || "";
      if (subStatus === "expired" || subStatus === "inactive") return "badge-danger";
      if (subStatus === "none") return "badge-secondary";
      if (subStatus === "expiring_soon" || subStatus === "trial") return "badge-outline-warning";
      return "badge-warning text-dark";
    },

    getCompanyHealthBadgeClass(comp) {
      const h = comp.health_status || "healthy";
      if (h === "inactive") return "badge-outline-danger";
      if (h === "warning_subscription" || h === "warning_low_activity") return "badge-outline-warning";
      return "badge-outline-success";
    },

    getCompanyHealthIcon(comp) {
      const h = comp.health_status || "healthy";
      if (h === "inactive") return "mdi-close-octagon-outline";
      if (h === "warning_subscription" || h === "warning_low_activity") return "mdi-alert-outline";
      return "mdi-check-circle-outline";
    },

    getCompanyHealthLabel(comp) {
      const h = comp.health_status || "healthy";
      if (h === "inactive") return this.$t("superadmin.healthInactive");
      if (h === "warning_subscription") return this.$t("superadmin.healthWarningSub");
      if (h === "warning_low_activity") return this.$t("superadmin.healthWarningActivity");
      return this.$t("superadmin.healthy");
    },

    async loadAllData() {
      this.loading = true;
      try {
        const [revResp, statsResp, compResp, billResp] = await Promise.allSettled([
          getSuperAdminRevenue(),
          getSuperAdminPlatformStats(),
          getSuperAdminCompanies({ limit: 15 }),
          getPendingBillingApprovals()
        ]);

        if (revResp.status === "fulfilled" && revResp.value?.data) {
          this.revenueData = revResp.value.data;
        }

        if (statsResp.status === "fulfilled" && statsResp.value?.data) {
          this.platformStats = statsResp.value.data;
        }

        if (compResp.status === "fulfilled" && compResp.value?.data) {
          const compData = compResp.value.data;
          this.companies = Array.isArray(compData) ? compData : (compData.data || []);
        }

        if (billResp.status === "fulfilled" && billResp.value?.data) {
          const billData = billResp.value.data;
          this.billingQueue = Array.isArray(billData) ? billData : (billData.data || []);
        }

        this.$nextTick(() => {
          this.renderRevenueChart();
        });
      } catch (err) {
        console.error("Error loading Superadmin dashboard telemetry:", err);
      } finally {
        this.loading = false;
      }
    },

    renderRevenueChart() {
      const isId = this.$currentLocale === "id";
      const monthNames = isId
        ? ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"]
        : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

      const monthly = this.revenueData.monthly_revenue || {};
      const chartData = [];
      for (let m = 1; m <= 12; m++) {
        chartData.push(monthly[m] || 0);
      }

      const container = document.getElementById("superadmin-revenue-chart");
      if (!container) return;

      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      const revenueAxisTitle = this.$t("superadmin.chartRevenueAxis");
      const revenueSeriesName = this.$t("superadmin.chartRevenueSeries");
      const localeTag = isId ? "id-ID" : "en-US";

      this.chartInstance = Highcharts.chart("superadmin-revenue-chart", {
        chart: {
          type: "column",
          backgroundColor: "#061531",
          style: {
            fontFamily: "Nunito, Plus Jakarta Sans, sans-serif"
          }
        },
        title: {
          text: ""
        },
        accessibility: {
          enabled: false
        },
        xAxis: {
          categories: monthNames,
          labels: {
            style: { color: "#93c5fd", fontSize: "11px" }
          },
          lineColor: "#1e4b85"
        },
        yAxis: {
          title: {
            text: revenueAxisTitle,
            style: { color: "#93c5fd", fontSize: "11px" }
          },
          labels: {
            formatter: function () {
              if (this.value >= 1000000) {
                return (this.value / 1000000).toFixed(1) + (isId ? " Jt" : "M");
              }
              return this.value;
            },
            style: { color: "#93c5fd", fontSize: "11px" }
          },
          gridLineColor: "rgba(56, 189, 248, 0.14)"
        },
        tooltip: {
          backgroundColor: "#0b2247",
          borderColor: "#f59e0b",
          borderRadius: 8,
          style: { color: "#ffffff" },
          formatter: function () {
            const val = new Intl.NumberFormat(localeTag, {
              style: "currency",
              currency: "IDR",
              minimumFractionDigits: 0
            }).format(this.y);
            return `<b>${this.x}</b><br/>${revenueSeriesName}: <span style="color: #fde047; font-weight: bold;">${val}</span>`;
          }
        },
        legend: {
          enabled: false
        },
        credits: {
          enabled: false
        },
        plotOptions: {
          column: {
            borderRadius: 4,
            color: "#fbbf24",
            pointPadding: 0.2
          }
        },
        series: [
          {
            name: revenueSeriesName,
            data: chartData
          }
        ]
      });
    },

    async handleApprove(item) {
      const invoiceNo = item.billing_transaction?.invoice_number || item.id;
      const compName = item.company?.name || this.$t("superadmin.clientFallback");
      const title = this.$t("superadmin.confirmActivationTitle");
      const text = this.$t("superadmin.confirmActivationText").replace("{invoice}", invoiceNo).replace("{company}", compName);

      const confirm = await Swal.fire({
        title: title,
        text: text,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#6366f1",
        cancelButtonColor: "#475569",
        confirmButtonText: this.$t("superadmin.confirmActivationBtn"),
        cancelButtonText: this.$t("common.cancel")
      });

      if (!confirm.isConfirmed) return;

      this.actionLoading = true;
      try {
        await approveBilling(item.id);
        Swal.fire({
          icon: "success",
          title: this.$t("common.success"),
          text: this.$t("superadmin.approveSuccessMsg", { company: compName }),
          timer: 2000,
          showConfirmButton: false
        });
        await this.loadAllData();
      } catch (err) {
        console.error("Approval error:", err);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: err.response?.data?.message || this.$t("superadmin.approveErrorMsg")
        });
      } finally {
        this.actionLoading = false;
      }
    },

    openRejectModal(item) {
      this.activeRejectItem = item;
      this.rejectReason = "";
      this.showRejectModal = true;
    },

    closeRejectModal() {
      this.showRejectModal = false;
      this.activeRejectItem = null;
      this.rejectReason = "";
    },

    async submitReject() {
      if (!this.rejectReason || !this.activeRejectItem) return;

      this.actionLoading = true;
      try {
        await rejectBilling(this.activeRejectItem.id, this.rejectReason);
        this.closeRejectModal();
        Swal.fire({
          icon: "info",
          title: this.$t("superadmin.invoiceRejectedTitle"),
          text: this.$t("superadmin.invoiceRejectedMsg"),
          timer: 2000,
          showConfirmButton: false
        });
        await this.loadAllData();
      } catch (err) {
        console.error("Reject error:", err);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: err.response?.data?.message || this.$t("superadmin.rejectErrorMsg")
        });
      } finally {
        this.actionLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.superadmin-dashboard {
  animation: fadeIn 0.25s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
